import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, 
  FaUser, 
  FaCalendarAlt,
  FaReply,
  FaExclamationTriangle,
  FaPaperPlane,
  FaEdit,
  FaTrash,
  FaEllipsisV,
  FaBan
} from 'react-icons/fa';
import { supabase } from '../../../data/supabaseClient';
import { AuthContext } from '../../../Provider';
import { useToast } from '../../../hooks/ToastContext';
import Spinner from '../../../common/Spinner';
import { CustomButton2, CustomButton8 } from '../../../Templates';
import '../../styles/GroupsPage.css';
import '../../styles/GroupDiscussionPage.css';

const GroupDiscussionDetailsPage = () => {
  const { id, discussionId } = useParams();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [discussion, setDiscussion] = useState(null);
  const [group, setGroup] = useState(null);
  const [comments, setComments] = useState([]);
  const [userMember, setUserMember] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showDiscussionMenu, setShowDiscussionMenu] = useState(false);
  const [showCommentMenu, setShowCommentMenu] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  
  // Load discussion data
  useEffect(() => {
    const fetchDiscussionData = async () => {
      if (!id || !discussionId) return;
      
      try {
        setLoading(true);
        
        // Fetch discussion details
        const { data: discussionData, error: discussionError } = await supabase
          .from('group_discussions')
          .select('*, profiles(*)')
          .eq('id', discussionId)
          .eq('group_id', id)
          .single();
        
        if (discussionError) throw discussionError;
        
        setDiscussion(discussionData);
        
        // Fetch group details
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('*')
          .eq('id', id)
          .single();
        
        if (groupError) throw groupError;
        
        setGroup(groupData);
        
        // Fetch comments
        const { data: commentsData, error: commentsError } = await supabase
          .from('group_discussion_comments')
          .select('*, profiles(*)')
          .eq('discussion_id', discussionId)
          .order('created_at', { ascending: true });
        
        if (commentsError) throw commentsError;
        
        setComments(commentsData || []);
        
        // Check if user is a member of the group
        if (user) {
          const { data: memberData, error: memberError } = await supabase
            .from('group_members')
            .select('*')
            .eq('group_id', id)
            .eq('user_id', user.id)
            .eq('status', 'active')
            .single();
          
          if (!memberError) {
            setUserMember(memberData);
            
            // If we're in edit mode, populate the edit content
            if (discussionData && (memberData.role === 'admin' || memberData.role === 'moderator' || discussionData.user_id === user.id)) {
              setEditContent(discussionData.content);
            }
          }
        }
        
      } catch (error) {
        console.error('Error fetching discussion data:', error);
        addToast({
          type: 'error',
          title: t('errors.fetch_error'),
          description: t('errors.discussion_fetch_failed')
        });
        navigate(`/groups/${id}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDiscussionData();
  }, [id, discussionId, user, navigate, t, addToast]);
  
  // Toggle discussion action menu
  const toggleDiscussionMenu = (e) => {
    e.stopPropagation();
    setShowDiscussionMenu(!showDiscussionMenu);
    setShowCommentMenu(null); // Close any open comment menus
  };
  
  // Toggle comment action menu
  const toggleCommentMenu = (e, commentId) => {
    e.stopPropagation();
    setShowCommentMenu(showCommentMenu === commentId ? null : commentId);
    setShowDiscussionMenu(false); // Close discussion menu
  };
  
  // Handle delete discussion
  const handleDeleteDiscussion = async () => {
    if (!user || !userMember || (userMember.role !== 'admin' && userMember.role !== 'moderator' && discussion.user_id !== user.id)) {
      return;
    }
    
    if (window.confirm(t('groups.confirm_delete_discussion'))) {
      setIsDeleting(true);
      
      try {
        const { error } = await supabase
          .from('group_discussions')
          .update({ status: 'deleted' })
          .eq('id', discussionId);
        
        if (error) throw error;
        
        addToast({
          type: 'success',
          title: t('success'),
          description: t('groups.discussion_deleted')
        });
        
        navigate(`/groups/${id}`);
      } catch (error) {
        console.error('Error deleting discussion:', error);
        addToast({
          type: 'error',
          title: t('errors.error'),
          description: t('errors.delete_failed')
        });
        setIsDeleting(false);
      }
    }
  };
  
  // Handle toggle edit mode
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
    setShowDiscussionMenu(false);
  };
  
  // Handle update discussion
  const handleUpdateDiscussion = async () => {
    if (!editContent.trim()) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('errors.content_required')
      });
      return;
    }
    
    setIsEditing(true);
    
    try {
      const { error } = await supabase
        .from('group_discussions')
        .update({ 
          content: editContent.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', discussionId);
      
      if (error) throw error;
      
      // Update local state
      setDiscussion({
        ...discussion,
        content: editContent.trim(),
        updated_at: new Date().toISOString()
      });
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.discussion_updated')
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating discussion:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.update_failed')
      });
    }
  };
  
  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    if (!user || !userMember) {
      return;
    }
    
    // Find the comment
    const comment = comments.find(c => c.id === commentId);
    if (!comment) return;
    
    // Check if user has permission (admin, moderator, or comment owner)
    if (userMember.role !== 'admin' && userMember.role !== 'moderator' && comment.user_id !== user.id) {
      addToast({
        type: 'error',
        title: t('errors.permission_denied'),
        description: t('errors.cannot_delete_comment')
      });
      return;
    }
    
    if (window.confirm(t('groups.confirm_delete_comment'))) {
      try {
        const { error } = await supabase
          .from('group_discussion_comments')
          .update({ status: 'deleted' })
          .eq('id', commentId);
        
        if (error) throw error;
        
        // Update local state
        setComments(comments.filter(c => c.id !== commentId));
        
        addToast({
          type: 'success',
          title: t('success'),
          description: t('groups.comment_deleted')
        });
      } catch (error) {
        console.error('Error deleting comment:', error);
        addToast({
          type: 'error',
          title: t('errors.error'),
          description: t('errors.delete_failed')
        });
      }
    }
    
    // Close comment menu
    setShowCommentMenu(null);
  };
  
  // Add a comment
  const handleAddComment = async () => {
    if (!user || !userMember) {
      addToast({
        type: 'error',
        title: t('errors.authentication_required'),
        description: t('errors.login_required_action')
      });
      return;
    }
    
    if (!newComment.trim()) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('errors.comment_required')
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Add the comment
      const { data: commentData, error: commentError } = await supabase
        .from('group_discussion_comments')
        .insert({
          discussion_id: discussionId,
          user_id: user.id,
          content: newComment.trim(),
          status: 'active'
        })
        .select('*, profiles(*)')
        .single();
      
      if (commentError) throw commentError;
      
      // Update UI
      setComments([...comments, commentData]);
      setNewComment('');
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.comment_added_success')
      });
      
    } catch (error) {
      console.error('Error adding comment:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.comment_failed')
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  // Navigate back to group
  const handleBack = () => {
    navigate(`/groups/${id}`);
  };
  
  if (loading) {
    return (
      <>
        <div className="groups-page">
          <Spinner size="large" />
        </div>
      </>
    );
  }
  
  if (!discussion) {
    return (
      <>
        <div className="groups-page">
          <div className="groups-empty">
            <h3>{t('groups.discussion_not_found')}</h3>
            <CustomButton2
              label={t('groups.back_to_group')}
              onClick={handleBack}
            />
          </div>
        </div>
      </>
    );
  }
  
  return (
    <>
      <div className="group-details-page">
        <div className="back-button" style={{ marginBottom: '20px' }}>
          <CustomButton8 onClick={handleBack}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <div className="discussion-details-container">
          <div className="discussion-header">
            <h1 className="discussion-title">{discussion.title}</h1>
            <div className="group-context">
              {t('groups.in_group')}: <strong>{group?.name}</strong>
            </div>
            
            <div className="discussion-meta">
              <div className="discussion-author">
                <FaUser className="icon" />
                <span>{discussion.profiles?.username || t('groups.unknown_user')}</span>
              </div>
              <div className="discussion-date">
                <FaCalendarAlt className="icon" />
                <span>{formatDate(discussion.created_at)}</span>
              </div>
            </div>
          </div>
          
          <div className="discussion-content">
            {/* Discussion actions menu for admin/moderator */}
            {userMember && (userMember.role === 'admin' || userMember.role === 'moderator' || discussion.user_id === user.id) && (
              <div className="discussion-actions">
                <button 
                  className="discussion-menu-button" 
                  onClick={toggleDiscussionMenu}
                  aria-label="Discussion actions"
                >
                  <FaEllipsisV />
                </button>
                
                {showDiscussionMenu && (
                  <div className="discussion-menu">
                    <button onClick={toggleEditMode}>
                      <FaEdit /> {t('groups.edit_discussion')}
                    </button>
                    <button onClick={handleDeleteDiscussion}>
                      <FaTrash /> {t('groups.delete_discussion')}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <div className="discussion-body">
              {isEditing ? (
                <div className="edit-discussion-form">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={10}
                  />
                  <div className="edit-actions">
                    <button onClick={() => setIsEditing(false)} className="cancel-button">
                      {t('buttons.cancel')}
                    </button>
                    <button onClick={handleUpdateDiscussion} className="save-button">
                      {t('buttons.save')}
                    </button>
                  </div>
                </div>
              ) : (
                discussion.content.split('\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))
              )}
            </div>
            
            <div className="discussion-comments">
              <h2 className="comments-title">
                {t('groups.comments')} ({comments.length})
              </h2>
              
              {comments.length === 0 ? (
                <div className="no-comments">
                  <p>{t('groups.no_comments_yet')}</p>
                </div>
              ) : (
                <div className="comments-list">
                  {comments.map(comment => (
                    <div key={comment.id} className="comment">
                      <div className="comment-header">
                        <div 
                          className="comment-author-avatar"
                          style={{
                            backgroundImage: comment.profiles?.avatar_url 
                              ? `url(${comment.profiles.avatar_url})` 
                              : 'none'
                          }}
                        />
                        <div className="comment-meta">
                          <div className="comment-author-name">
                            {comment.profiles?.username || t('groups.unknown_user')}
                          </div>
                          <div className="comment-date">
                            {formatDate(comment.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className="comment-content">
                        {comment.content.split('\n').map((paragraph, i) => (
                          <p key={i}>{paragraph}</p>
                        ))}
                        
                        {/* Comment actions for admins/moderators/owner */}
                        {userMember && (userMember.role === 'admin' || userMember.role === 'moderator' || comment.user_id === user.id) && (
                          <div className="comment-actions">
                            <button 
                              className="comment-menu-button" 
                              onClick={(e) => toggleCommentMenu(e, comment.id)}
                              aria-label="Comment actions"
                            >
                              <FaEllipsisV />
                            </button>
                            
                            {showCommentMenu === comment.id && (
                              <div className="comment-menu">
                                <button onClick={() => handleDeleteComment(comment.id)}>
                                  <FaTrash /> {t('groups.delete_comment')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {userMember ? (
                <div className="add-comment">
                  <h3 className="add-comment-title">
                    <FaReply />
                    {t('groups.add_comment')}
                  </h3>
                  
                  <div className="comment-form">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={t('groups.comment_placeholder')}
                      rows={4}
                    />
                    
                    <div className="comment-actions">
                      <CustomButton2
                        label={submitting ? t('app.submitting') : t('groups.submit_comment')}
                        onClick={handleAddComment}
                        disabled={submitting || !newComment.trim()}
                        icon={submitting ? <Spinner size="small" showText={false} /> : <FaPaperPlane />}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="login-to-comment">
                  <FaExclamationTriangle />
                  <p>{t('groups.login_to_comment')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GroupDiscussionDetailsPage;
